// src/pages/customer/CustomerProfile.jsx
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import BottomNav from '../../components/BottomNav';
import Icon from '../../components/Icon';
import LanguageSelect from '../../components/LanguageSelect';
import { MyCodeSheet } from './CustomerDebts';
import { useSupport } from '../../hooks/useSupport';
import { useToast } from '../../hooks/useToast';
import { CUSTOMER_NAV, useCustomerNav } from './CustomerHome';
import { isValidWhatsapp } from '../../utils/whatsapp';
import DeleteAccountSheet, { DeleteAccountLink } from '../../components/DeleteAccountSheet';

export default function CustomerProfile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, updateProfile } = useAuth();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const { show, ToastView } = useToast();
  const navItems = useCustomerNav();
  const [editing, setEditing] = useState(false);
  // Личный код: магазин сканирует его, чтобы записать долг на меня
  const [codeOpen, setCodeOpen] = useState(false);
  const { contacts } = useSupport();
  const [name, setName] = useState(user?.name || '');
  const [whatsapp, setWhatsapp] = useState(user?.whatsapp || '');
  const [saving, setSaving] = useState(false);

  // Номер можно оставить пустым, но если вписан — проверяем (как на бэке)
  const waOk = !whatsapp.trim() || isValidWhatsapp(whatsapp);

  const save = async () => {
    if (!waOk) return;
    setSaving(true);
    try {
      await updateProfile({ name: name.trim(), whatsapp: whatsapp.trim() });
      show(t('settings.saved'));
      setEditing(false);
    } catch (e) { show(e.message, 'error'); }
    setSaving(false);
  };

  return (
    <div className="page">
      {ToastView}
      <div className="page-header">
        <div>
          <p className="page-subtitle">{t('customerProfile.title').toUpperCase()}</p>
          <h1 className="page-title">{t('customerProfile.title')}</h1>
        </div>
      </div>

      <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{
              width: 56, height: 56, borderRadius: 'var(--r-md)',
              background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-accent))',
              color: '#fff', fontSize: 24, fontWeight: 800,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'var(--font-display)',
            }}>
              {(user?.name || user?.email || '?')[0].toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: 16, fontWeight: 700 }}>{user?.name || t('customerProfile.noName')}</p>
              <p style={{ fontSize: 12, color: 'var(--fg-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user?.email}
              </p>
              <p style={{ fontSize: 12, color: user?.whatsapp ? 'var(--fg-muted)' : 'var(--warning, #c77700)', display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                <Icon name="whatsapp" size={12} /> {user?.whatsapp || t('customerProfile.noWhatsapp')}
              </p>
            </div>
          </div>

          {editing ? (
            <>
              <input
                className="input"
                placeholder={t("auth.namePlaceholder")}
                value={name}
                onChange={e => setName(e.target.value)}
                style={{ marginBottom: 8 }}
              />
              <input
                className="input"
                type="tel"
                inputMode="tel"
                placeholder={t('customerProfile.whatsappPlaceholder')}
                value={whatsapp}
                onChange={e => setWhatsapp(e.target.value)}
                style={{ marginBottom: 8, borderColor: waOk ? undefined : 'var(--danger)' }}
              />
              <p style={{ fontSize: 11, color: 'var(--fg-muted)', marginBottom: 8, lineHeight: 1.4 }}>
                {t('customerProfile.whatsappHint')}
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => { setEditing(false); setName(user?.name || ''); setWhatsapp(user?.whatsapp || ''); }} style={{ flex: 1 }}>
                  {t('common.cancel')}
                </button>
                <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || !waOk} style={{ flex: 1 }}>
                  {saving ? '…' : t('common.save')}
                </button>
              </div>
            </>
          ) : (
            <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>
              <Icon name="edit" size={14} />
              {t('customerProfile.editProfile')}

            </button>
          )}
        </div>

        {/* Личный код и долги */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <button
            onClick={() => setCodeOpen(true)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 12,
              padding: 14, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <Icon name="qr" size={20} color="var(--brand-primary)" />
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 14, fontWeight: 600 }}>{t('myCode.title')}</p>
              <p style={{ fontSize: 11, color: 'var(--fg-muted)' }}>{t('myCode.short')}</p>
            </div>
            <Icon name="chevronRight" size={16} color="var(--fg-muted)" />
          </button>

          <button
            onClick={() => navigate('/my-debts')}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 12,
              padding: 14, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
            }}
          >
            <Icon name="wallet" size={20} color="var(--danger)" />
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 14, fontWeight: 600 }}>{t('myDebts.title')}</p>
              <p style={{ fontSize: 11, color: 'var(--fg-muted)' }}>{t('myDebts.short')}</p>
            </div>
            <Icon name="chevronRight" size={16} color="var(--fg-muted)" />
          </button>
        </div>

        {/* Связь с разработчиком */}
        <div className="card">
          <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-muted)', marginBottom: 8, letterSpacing: '0.08em' }}>
            {t('support.title')}
          </p>
          <p style={{ fontSize: 12, color: 'var(--fg-muted)', marginBottom: 12, lineHeight: 1.5 }}>
            {t('support.hint')}
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            {contacts.whatsapp && (
              <a className="btn btn-secondary" href={contacts.whatsapp} target="_blank" rel="noreferrer"
                style={{ flex: 1, textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <Icon name="whatsapp" size={16} /> WhatsApp
              </a>
            )}
            {contacts.telegram && (
              <a className="btn btn-secondary" href={contacts.telegram} target="_blank" rel="noreferrer"
                style={{ flex: 1, textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <Icon name="share" size={16} /> Telegram
              </a>
            )}
          </div>
        </div>

        <div className="card">
          <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-muted)', marginBottom: 12, letterSpacing: '0.08em' }}>{t('common.appearance')}</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <button onClick={() => setTheme('light')} style={themeBtn(theme === 'light')}>
              <Icon name="sun" size={16} />
              {t('common.themeLight')}
            </button>
            <button onClick={() => setTheme('dark')} style={themeBtn(theme === 'dark')}>
              <Icon name="moon" size={16} />
              {t('common.themeDark')}
            </button>
          </div>
        </div>

        <div className="card">
          <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-muted)', marginBottom: 12, letterSpacing: '0.08em' }}>{t('common.language')}</p>
          <LanguageSelect />
        </div>

        <button
          onClick={() => { if (window.confirm(t('settings.logoutConfirm'))) { logout(); navigate('/'); } }}
          className="btn btn-ghost"
          style={{ color: 'var(--danger)' }}
        >
          <Icon name="logout" size={16} />
          {t('common.logout')}
        </button>
        <DeleteAccountLink onClick={() => setDeleteOpen(true)} />

        <div style={{ height: 20 }} />
      </div>

      {codeOpen && <MyCodeSheet onClose={() => setCodeOpen(false)} />}
      {deleteOpen && (
        <DeleteAccountSheet
          isShop={false}
          onClose={() => setDeleteOpen(false)}
          onDeleted={() => { logout(); navigate('/'); }}
        />
      )}

      <BottomNav items={navItems} />
    </div>
  );
}

const themeBtn = (active) => ({
  padding: 12, borderRadius: 'var(--r-sm)',
  border: '1px solid', borderColor: active ? 'var(--brand-primary)' : 'var(--border)',
  background: active ? 'var(--brand-primary-soft)' : 'var(--bg-subtle)',
  color: active ? 'var(--brand-primary)' : 'var(--fg-muted)',
  fontSize: 14, fontWeight: 600, cursor: 'pointer',
  fontFamily: 'var(--font-display)',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
});

const menuBtn = {
  width: '100%', display: 'flex', alignItems: 'center', gap: 12,
  padding: '12px 4px', background: 'none', border: 'none',
  fontSize: 14, fontWeight: 600, color: 'var(--fg)',
  cursor: 'pointer', fontFamily: 'var(--font-display)',
};
